import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Checkbox, Drawer, Form, Select, Space } from 'antd';
import debounce from 'lodash.debounce';
import { useGetExpenseTypes } from 'api/expenses/use-get-expense-types.ts';
import { filterRangePresets } from 'shared/constants/filters';
import type { IExpensesFilter } from 'shared/stores/use-expenses-filters.ts';
import { useExpensesFiltersStore } from 'shared/stores/use-expenses-filters.ts';
import { transformAndSetFilters } from 'shared/utils/filters/transform-and-set-filters.ts';
import { ExpenseStatuses } from 'types/expense-status';
import { ClientFormSelect } from 'components/common/client-form-select';
import { DataRangeFormPicker } from 'components/common/data-range-form-picker';
import styles from './expenses-filters-drawer.module.scss';
import type { IExpensesFiltersProps } from './types';

export const ExpensesFiltersDrawer: React.FC<IExpensesFiltersProps> = (props) => {
  const { form, isOpen, onClose } = props;
  const { t } = useTranslation();
  const { filters, search, setFilters } = useExpensesFiltersStore();

  const { data: expenseTypes } = useGetExpenseTypes();

  const overdue = Form.useWatch('overdue', form);

  const handleSuccess = debounce(() => transformAndSetFilters<IExpensesFilter>(form, setFilters), 300);

  const handleClearFilters = () => {
    setFilters(undefined);
    setTimeout(() => form.resetFields(), 0);
    onClose();
  };

  const clearButton = filters ? (
    <Button type='link' size='small' onClick={handleClearFilters}>
      {t('common:clear_filters')}
    </Button>
  ) : null;

  return (
    <Drawer placement='right' open={isOpen} extra={[clearButton]} onClose={onClose}>
      <Form form={form} name='expenses-filter' layout='horizontal' initialValues={filters}>
        <Space direction='vertical' size='middle' className={styles.box}>
          <Form.Item name='typeId' className={styles.input}>
            <Select placeholder='Type' disabled={!!search} allowClear onChange={handleSuccess}>
              {expenseTypes?.map((type) => (
                <Select.Option key={type.id} value={type.id}>
                  {type.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name='statuses' className={styles.input}>
            <Select
              placeholder='Statuses'
              mode='multiple'
              disabled={!!search || overdue}
              allowClear
              onChange={handleSuccess}>
              {ExpenseStatuses.map((status) => (
                <Select.Option key={status} value={status}>
                  {status}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <ClientFormSelect
            name='clientId'
            placeholder='Partner'
            handleClientChange={handleSuccess}
            className={styles.input}
            currentClientId={filters?.clientId?.value}
            disabled={!!search}
            allowClear
          />
          <DataRangeFormPicker
            name='endDate'
            handleSuccess={handleSuccess}
            isRequired={false}
            className={styles.input}
            rangePresets={filterRangePresets}
            disabled={!!search || overdue}
          />
          <Form.Item name='overdue' className={styles.input} valuePropName='checked'>
            <Checkbox disabled={!!search} onChange={handleSuccess}>
              Overdue
            </Checkbox>
          </Form.Item>
        </Space>
      </Form>
    </Drawer>
  );
};
